import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from '../badRequestHandler';

// Create the sports API service
export const sportsApi = createApi({
  reducerPath: 'sportsApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Sports', 'Bets'],
  endpoints: (builder) => ({
    // Get cricket detail endpoint
    getCricketDetail: builder.query({
      query: ({ from, to, filter, page = 1, limit = 20 }) => {
        let url = `/api/cricket-detail?from=${from}&to=${to}&page=${page}&limit=${limit}`;
        if (filter) {
          url += `&filter=${filter}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Sports'],
    }),

    // Get rejected/cancelled bets endpoint
    getRejectedCancelledBets: builder.query({
      query: ({ eventId, betType = 'fancy', status = 'all' }) => ({
        url: `/api/rejected-cancelled-bets?beventId=${eventId}&betType=${betType}&status=${status}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get fancy profit loss endpoint
    getFancyProfitLoss: builder.query({
      query: ({ eventId, fancyId, search, page = 1, limit = 50 }) => {
        let url = `/api/fancy-profit-loss?beventId=${eventId}&page=${page}&limit=${limit}`;
        if (fancyId) {
          url += `&fancyId=${fancyId}`;
        }
        if (search) {
          url += `&userId=${search}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Bets'],
    }),

    // Get fancy bets endpoint
    getFancyBets: builder.query({
      query: ({ eventId, fancyId, search, page = 1, limit = 50 }) => {
        let url = `/api/fancy-bets?beventId=${eventId}&page=${page}&limit=${limit}`;
        if (fancyId) {
          url += `&fancyId=${fancyId}`;
        }
        if (search) {
          url += `&search=${search}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Bets'],
    }),

    // Get fancy dropdown endpoint
    getFancyDropdown: builder.query({
      query: ({ eventId }) => ({
        url: `/api/admin/fancy-dropdown/${eventId}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get bookmaker bets endpoint
    getBookmakerBets: builder.query({
      query: ({ eventId, clientId, page = 1, limit = 50 }) => {
        let url = `/api/market-exposure?beventId=${eventId}&page=${page}&limit=${limit}`;
        if (clientId) {
          url += `&clientId=${clientId}`;
        }
        return {
          url,
          method: 'GET',
        };
      },
      providesTags: ['Bets'],
    }),

    // Get event summary endpoint
    getEventSummary: builder.query({
      query: ({ eventId, filter = 'all' }) => ({
        url: `/api/event-summary?beventId=${eventId}&filter=${filter}`,
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    // Get event bets endpoint
    getEventBets: builder.query({
      query: ({ eventId, page = 1, limit = 50 }) => ({
        url: `/api/event-bets?beventId=${eventId}&page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get fancy session summary endpoint
    getFancySessionSummary: builder.query({
      query: ({ eventId, page = 1, limit = 50 }) => ({
        url: `/api/fancy-session-summary?beventId=${eventId}&page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get client PnL endpoint
    getClientPnl: builder.query({
      query: ({ eventId, userId, fancyId }) => ({
        url: `/api/downline-pnl?beventId=${eventId}&userId=${userId}&fancyId=${fancyId}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get company report event endpoint
    getCompanyReportEvent: builder.query({
      query: ({ eventId, userId }) => ({
        url: `/api/company-report-event?beventId=${eventId}${userId ? `&userId=${userId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    // Get fancy book endpoint
    getFancyBook: builder.query({
      query: ({ fancyId, beventId }) => ({
        url: `/api/fancy/book/admin?fancyId=${fancyId}&beventId=${beventId}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get fancies with bets endpoint
    getFanciesWithBets: builder.query({
      query: (eventId) => ({
        url: `/api/fancies/with-bets/${eventId}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get fancy downline P&L endpoint
    getFancyDownlinePnl: builder.query({
      query: ({ eventId, page = 1, limit = 50 }) => ({
        url: `/api/fancy-downline-pnl?beventId=${eventId}&page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      providesTags: ['Bets'],
    }),

    // Get live cricket score endpoint
    getCricketScoreLive: builder.query({
      query: ({ beventId }) => ({
        url: `/api/cricket-scores/live?beventId=${beventId}`,
        method: 'GET',
      }),
    }),

    // Get toss exposure bets endpoint
    getTossExposure: builder.query({
      query: ({ eventId, clientId, page = 1, limit = 50 }) => {
        let url = `/api/toss-exposure?beventId=${eventId}&page=${page}&limit=${limit}`;
        if (clientId) url += `&clientId=${clientId}`;
        return { url, method: 'GET' };
      },
      providesTags: ['Bets'],
    }),

    // Freeze event / create ledger
    freezeEvent: builder.mutation({
      query: (eventId) => ({
        url: `/api/admin/freeze-event/${eventId}`,
        method: 'POST',
      }),
      invalidatesTags: ['Sports'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetCricketDetailQuery,
  useGetRejectedCancelledBetsQuery,
  useGetFancyProfitLossQuery,
  useGetFancyBetsQuery,
  useGetFancyDropdownQuery,
  useGetBookmakerBetsQuery,
  useGetEventSummaryQuery,
  useGetEventBetsQuery,
  useGetFancySessionSummaryQuery,
  useGetClientPnlQuery,
  useGetCompanyReportEventQuery,
  useGetFancyBookQuery,
  useGetFanciesWithBetsQuery,
  useGetFancyDownlinePnlQuery,
  useGetCricketScoreLiveQuery,
  useGetTossExposureQuery,
  useFreezeEventMutation,
} = sportsApi;

export default sportsApi;
